import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  getCountFromServer,
  query,
  where,
  limit,
} from "firebase/firestore";
import type { Brand, Vehicle, VehicleCategory } from "@oriental-ramirez/shared";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";

export async function getAllVehicles(): Promise<Vehicle[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.VEHICLES));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Vehicle));
}

export type VehicleFilters = {
  type?: "Motocicleta" | "Carro";
  categoryId?: string;
  brandId?: string;
  search?: string;
};

export type VehicleSort = "name" | "year" | "createdAt" | "salePrice";

export async function getVehicleById(id: string): Promise<Vehicle | null> {
  const ref = doc(db, COLLECTIONS.VEHICLES, id);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Vehicle) : null;
}

export async function addVehicle(vehicle: Omit<Vehicle, "id">): Promise<Vehicle> {
  const docRef = doc(collection(db, COLLECTIONS.VEHICLES));
  const vehicleWithId = {
    ...vehicle,
    id: docRef.id,
    createdAt: vehicle.createdAt ?? new Date(),
    updatedAt: new Date(),
  };
  await setDoc(docRef, vehicleWithId);
  return vehicleWithId as Vehicle;
}

export async function updateVehicle(id: string, data: Partial<Vehicle>): Promise<void> {
  const ref = doc(db, COLLECTIONS.VEHICLES, id);
  await updateDoc(ref, { ...data, id, updatedAt: new Date() });
}

export async function deleteVehicle(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.VEHICLES, id));
}

export async function getAllCategories(): Promise<VehicleCategory[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.CATEGORIES));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as VehicleCategory));
}

export async function getCategoryById(id: string): Promise<VehicleCategory | null> {
  const ref = doc(db, COLLECTIONS.CATEGORIES, id);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as VehicleCategory) : null;
}

export async function addCategory(category: Omit<VehicleCategory, "id">): Promise<string> {
  const docRef = doc(collection(db, COLLECTIONS.CATEGORIES));
  const withId = { ...category, id: docRef.id, createdAt: new Date(), updatedAt: new Date() };
  await setDoc(docRef, withId);
  return docRef.id;
}

export async function updateCategory(id: string, data: Partial<VehicleCategory>): Promise<void> {
  const ref = doc(db, COLLECTIONS.CATEGORIES, id);
  await updateDoc(ref, { ...data, id, updatedAt: new Date() });
}

export async function deleteCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.CATEGORIES, id));
}

export async function getAllBrands(): Promise<Brand[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.BRANDS));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Brand));
}

export async function getBrandById(id: string): Promise<Brand | null> {
  const ref = doc(db, COLLECTIONS.BRANDS, id);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Brand) : null;
}

export async function addBrand(brand: Omit<Brand, "id">): Promise<string> {
  const docRef = doc(collection(db, COLLECTIONS.BRANDS));
  const withId = { ...brand, id: docRef.id, createdAt: new Date(), updatedAt: new Date() };
  await setDoc(docRef, withId);
  return docRef.id;
}

export async function updateBrand(id: string, data: Partial<Brand>): Promise<void> {
  const ref = doc(db, COLLECTIONS.BRANDS, id);
  await updateDoc(ref, { ...data, id, updatedAt: new Date() });
}

export async function deleteBrand(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.BRANDS, id));
}

// --- Panel optimizado (0 document reads para conteos) ---

export async function getVehicleCount(): Promise<number> {
  const snapshot = await getCountFromServer(collection(db, COLLECTIONS.VEHICLES));
  return snapshot.data().count;
}

export async function getVehiclesByTypeBreakdown(): Promise<{ type: string; count: number }[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.VEHICLES));
  const counts: Record<string, number> = {};
  snapshot.docs.forEach((d) => {
    const type = (d.data().type as string) || "Motocicleta";
    counts[type] = (counts[type] || 0) + 1;
  });
  return Object.entries(counts).map(([type, count]) => ({ type, count }));
}

export async function getTopVehicles(max = 5): Promise<Vehicle[]> {
  const q = query(
    collection(db, COLLECTIONS.VEHICLES),
    where("featured", "==", true),
    limit(max)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Vehicle));
}
